import React, { useState } from 'react';

import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import Image from '~/components/Common/Image';
import { TrendingPlace, defaultColumns } from '~/components/Content/TrendingPlaces/Columns';
import ManualPositionCell from '~/components/Content/TrendingPlaces/ManualPositionCell';

interface Props {
  trendingPlaces: Array<TrendingPlace>;
  isLoading: boolean;
  handleUpdate: (row: TrendingPlace) => void;
  onEnforcedPositionChange: (row: TrendingPlace, newValue: boolean) => void;
  onManualPositionChange: (row: TrendingPlace, manualPosition?: number | null) => Promise<boolean>;
}

const TrendingPlacesTable = ({
  trendingPlaces,
  isLoading,
  handleUpdate,
  onEnforcedPositionChange,
  onManualPositionChange,
}: Props) => {
  const [selectedImage, setSelectedImage] = useState<{ publicId: string; placeName: string } | null>(null);

  const enforceColumn: GridColDef<TrendingPlace> = {
    field: 'enforcedPosition',
    headerName: 'Enforce Position',
    width: 100,
    sortable: false,
    disableColumnMenu: true,
    display: 'flex',
    renderCell: (params) => (
      <Tooltip title="Keep this destination in its configured slot when personalised destinations are shown.">
        <Checkbox
          checked={params.row.enforcedPosition ?? false}
          onChange={(e) => onEnforcedPositionChange(params.row, e.target.checked)}
          inputProps={{ 'aria-label': `Keep ${params.row.placeName} in its configured slot` }}
        />
      </Tooltip>
    ),
  };

  const updateColumn: GridColDef<TrendingPlace> = {
    field: 'update',
    headerName: 'Update',
    align: 'center',
    sortable: false,
    disableColumnMenu: true,
    display: 'flex',
    headerAlign: 'center',
    width: 104,
    renderCell: (params) => (
      <Button variant="outlined" color="primary" onClick={() => handleUpdate(params.row)}>
        Edit
      </Button>
    ),
  };

  const columns: Array<GridColDef<TrendingPlace>> = [
    updateColumn,
    ...defaultColumns.flatMap((column) => {
      if (column.field !== 'manualPosition') {
        return column;
      }

      return [
        {
          ...column,
          renderCell: (params) => <ManualPositionCell row={params.row} onSave={onManualPositionChange} />,
        },
        enforceColumn,
      ];
    }),
    {
      field: 'placeId',
      headerName: 'Place Id',
      minWidth: 280,
      flex: 1,
      display: 'flex',
      renderCell: (params) => (
        <Tooltip title={params.row.placeId}>
          <Typography noWrap variant="body2">
            {params.row.placeId}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'imageId',
      headerName: 'Image',
      width: 104,
      renderCell: (params) => {
        return params.row.imageId ? (
          <ButtonBase
            aria-label={`View image for ${params.row.placeName}`}
            onClick={() => setSelectedImage({ publicId: params.row.imageId, placeName: params.row.placeName })}
            sx={{ borderRadius: 1, overflow: 'hidden' }}
          >
            <Image options={{ width: 72 }} publicId={params.row.imageId} loading="lazy" />
          </ButtonBase>
        ) : (
          <Typography color="text.secondary" variant="body2">
            No image
          </Typography>
        );
      },
      display: 'flex',
    },
  ];

  return (
    <Box>
      <Dialog open={Boolean(selectedImage)} onClose={() => setSelectedImage(null)} fullWidth maxWidth="md">
        <DialogTitle>{selectedImage?.placeName} image</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center' }}>
          {selectedImage && (
            <Image
              alt={`Destination image for ${selectedImage.placeName}`}
              options={{ width: 720 }}
              publicId={selectedImage.publicId}
              style={{ height: 'auto', maxWidth: '100%' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <DataGrid
        columns={columns}
        className="T-offers-table"
        rows={trendingPlaces || []}
        loading={isLoading}
        getRowId={(row: TrendingPlace) => row.id}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        disableColumnMenu
        autoHeight
      />
    </Box>
  );
};

export default TrendingPlacesTable;
